import { useEffect, useRef, useState, useCallback, ClipboardEvent, KeyboardEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { showError, showSuccess } from '@/lib/toast';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

export function VerifyOtp() {
  const navigate = useNavigate();
  const location = useLocation();
  const verifyOtp = useAuthStore((s) => s.verifyOtp);

  // The email can come from router state (preferred) or ?email= query param
  // (fallback if the user refreshed or followed a link).
  const stateEmail = (location.state as { email?: string } | null)?.email;
  const queryEmail = new URLSearchParams(location.search).get('email') || undefined;
  const email = stateEmail || queryEmail || '';

  const [digits, setDigits] = useState<string[]>(() => Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  // If the user landed here without an email (e.g. refreshed page), send them back.
  useEffect(() => {
    if (!email) {
      navigate('/register', { replace: true });
    }
  }, [email, navigate]);

  // Auto-focus the first empty input on mount
  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  // Resend cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const submitCode = useCallback(
    async (code: string) => {
      if (code.length !== OTP_LENGTH || loading) return;
      setLoading(true);
      setErrorMessage(null);
      try {
        await verifyOtp(email, code);
        showSuccess('Email verified — welcome!');
        navigate('/dashboard', { replace: true });
      } catch (error: any) {
        const msg =
          error.response?.data?.message ||
          error.message ||
          'Verification failed. Please try again.';
        const text = Array.isArray(msg) ? msg.join(', ') : msg;
        setErrorMessage(text);
        showError(text);
        // Clear the inputs so the user can retype
        setDigits(Array(OTP_LENGTH).fill(''));
        inputsRef.current[0]?.focus();
      } finally {
        setLoading(false);
      }
    },
    [email, loading, navigate, verifyOtp],
  );

  const handleChange = (index: number, value: string) => {
    // Only allow digits; take just the last character typed
    const sanitized = value.replace(/\D/g, '').slice(-1);
    if (!sanitized && value !== '') return;

    const next = [...digits];
    next[index] = sanitized;
    setDigits(next);
    if (errorMessage) setErrorMessage(null);

    // Auto-advance
    if (sanitized && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }

    // Auto-submit when all boxes are filled
    if (sanitized && index === OTP_LENGTH - 1) {
      const code = next.join('');
      if (code.length === OTP_LENGTH) {
        void submitCode(code);
      }
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      // Backspace on an empty box moves focus back
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    } else if (e.key === 'Enter') {
      const code = digits.join('');
      if (code.length === OTP_LENGTH) void submitCode(code);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputsRef.current[focusIndex]?.focus();
    if (pasted.length === OTP_LENGTH) {
      void submitCode(pasted);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setErrorMessage(null);
    try {
      await api.post('/auth/resend-otp', { email });
      showSuccess('A new code has been sent to your email.');
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setDigits(Array(OTP_LENGTH).fill(''));
      inputsRef.current[0]?.focus();
    } catch (error: any) {
      const msg =
        error.response?.data?.message ||
        error.message ||
        'Could not resend the code. Please try again.';
      const text = Array.isArray(msg) ? msg.join(', ') : msg;
      setErrorMessage(text);
      showError(text);
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submitCode(digits.join(''));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <Link to="/" className="flex flex-col items-center gap-3">
            <img src="/logo.png" alt="SetOpenClaw" className="h-20 w-20 rounded-2xl object-contain" />
            <span className="text-2xl font-bold">SetOpenClaw</span>
          </Link>
        </div>

        <Card>
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Verify your email</h1>
              <p className="text-sm text-muted-foreground">
                We sent a 6-digit code to{' '}
                <span className="font-medium text-foreground">{email}</span>.
                <br />
                Enter it below to finish creating your account.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {errorMessage && (
                <div
                  role="alert"
                  className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {errorMessage}
                </div>
              )}

              <div className="flex justify-center gap-2" onPaste={handlePaste}>
                {digits.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      inputsRef.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    disabled={loading}
                    aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
                    className="h-14 w-12 rounded-md border border-input bg-background text-center text-2xl font-semibold tracking-tight outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
                  />
                ))}
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading || digits.join('').length !== OTP_LENGTH}
              >
                {loading ? 'Verifying...' : 'Verify & Continue'}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              Didn't receive the code?{' '}
              <button
                type="button"
                onClick={handleResend}
                disabled={cooldown > 0 || resending}
                className="text-primary font-medium hover:underline disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed"
              >
                {resending
                  ? 'Sending...'
                  : cooldown > 0
                    ? `Resend in ${cooldown}s`
                    : 'Resend code'}
              </button>
            </div>

            <div className="mt-4 text-center">
              <Link
                to="/login"
                className="text-sm text-primary hover:underline inline-flex items-center gap-1"
              >
                <ArrowLeft className="h-3 w-3" />
                Back to login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
